import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { MobilePatientList, MobileRowVM } from './pp/MobilePatientList';
import RowActionsMenu from './RowActionsMenu';

// Mobile patients list from the design (§05): alphabetical groups, no per-row action buttons, infinite scroll instead of pages.
interface ApiPatient { id: number; name: string; summary: { next: { start: string; procedure: string | null } | null; last: { start: string; procedure: string | null } | null; package: { remaining: number; total: number } | null; recallDue: boolean; alert: string | null } }
const PAGE_SIZE = 30;
const CHIPS = ['all', 'recall_due', 'no_appointment'] as const;

interface Props {
  refreshKey: number;
  onOpenWorkspace: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, name: string) => void;
  onAnonymize?: (id: number, name: string) => void;
  onChat?: (id: number) => void;
  onSchedule?: (id: number) => void;
}

const PatientsMobile: React.FC<Props> = ({ refreshKey, onOpenWorkspace, onEdit, onDelete, onAnonymize, onChat, onSchedule }) => {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<string>('all');
  const [rows, setRows] = useState<ApiPatient[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const sentinel = useRef<HTMLDivElement>(null);

  const load = useCallback(async (p: number, replace: boolean) => {
    const params = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
    if (q.trim()) params.set('q', q.trim());
    if (chip !== 'all') params.set('filter', chip);
    try {
      const res: any = await apiService.get(`/api/patients?${params}`);
      if (res.success) {
        setRows(cur => (replace ? res.data : [...cur, ...res.data]));
        setTotalPages(res.pagination?.totalPages ?? 1);
      }
    } catch { /* keep rows */ }
  }, [q, chip]);

  // reset to page 1 on any query change / external refresh (debounced while typing)
  useEffect(() => {
    setPage(1);
    const id = setTimeout(() => load(1, true), q ? 250 : 0);
    return () => clearTimeout(id);
  }, [load, q, refreshKey]);

  // infinite scroll
  useEffect(() => {
    const el = sentinel.current;
    if (!el || page >= totalPages) return;
    const io = new IntersectionObserver(es => { if (es[0].isIntersecting) { setPage(p => p + 1); load(page + 1, false); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [page, totalPages, load]);

  const day = (iso: string) => new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: 'short' }).format(new Date(iso));
  const nextLabel = (iso: string) => new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  const toRow = (p: ApiPatient): MobileRowVM => {
    const s = p.summary;
    return {
      id: p.id, name: p.name, initials: p.name.split(/\s+/).filter(w => /^\p{L}/u.test(w)).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
      sub: s.next ? t('patientsHub.nextShort', { when: nextLabel(s.next.start) }) : s.last ? `${s.last.procedure || t('patientsHub.firstVisit')} · ${day(s.last.start)}` : t('patientsHub.noAppointment'),
      alert: !!s.alert,
      badge: s.recallDue ? { text: t('patientsHub.recallLate'), bg: '#f3e3df', color: '#8b4b3d' } : undefined,
      plain: !s.recallDue && s.package ? `${s.package.remaining}/${s.package.total}` : undefined,
    };
  };

  return (
    <div style={{ background: 'var(--bg,#f7f4ee)', marginLeft: 'calc(-1 * max(12px, calc(12px + env(safe-area-inset-left))))', marginRight: 'calc(-1 * max(12px, calc(12px + env(safe-area-inset-right))))' }}>
      <MobilePatientList
        title={t('patientsHub.patients')} search={q} onSearch={setQ} searchPlaceholder={t('patientsHub.searchMobile')}
        chips={CHIPS.map(k => ({ key: k, label: t(`patientsHub.chip.${k}`) }))} chip={chip} onChip={setChip}
        rows={rows.map(toRow)} onSelect={onOpenWorkspace}
        swipeActions={r => [
          ...(onChat ? [{ label: 'WhatsApp', onClick: () => onChat(r.id), bg: '#3d6e4f' }] : []),
          { label: t('patientsHub.schedule'), onClick: () => (onSchedule ? onSchedule(r.id) : onOpenWorkspace(r.id)), bg: '#0b3b2e' },
        ]}
        renderMenu={(r) => {
          const actions = [
            onChat && { label: 'WhatsApp', onClick: () => onChat(r.id) },
            onEdit && { label: t('common.edit'), onClick: () => onEdit(r.id) },
            onAnonymize && { label: t('clients.anonymize', 'Anonimizar (LGPD)'), onClick: () => onAnonymize(r.id, r.name), danger: true },
            onDelete && { label: t('common.delete'), onClick: () => onDelete(r.id, r.name), danger: true },
          ].filter(Boolean) as { label: string; onClick: () => void; danger?: boolean }[];
          return actions.length ? <RowActionsMenu label={t('clients.rowMenu')} actions={actions} /> : null;
        }}
      />
      <div ref={sentinel} style={{ height: 1 }} />
    </div>
  );
};

export default PatientsMobile;
